import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import * as React from "react";

import { TriggerForEachPrimitive } from "@/shared/domain/actions/TriggerRecord";

import styles from "./ResourcePrefixSelector.module.css";
import { type ResourcePrefix } from "./utils";

interface ResourcePrefixSelectorProps {
  value: ResourcePrefix;
  onChange: (value: ResourcePrefix) => void;
  forEachPrimitive: TriggerForEachPrimitive;
  disabled?: boolean;
}

/**
 * Dropdown selector for choosing a resource prefix for trigger conditions.
 *
 * Options:
 * - "dataset": Explicitly targets dataset properties
 * - "file": Targets file properties (only available for per-file triggers)
 *
 * The "File" option is disabled when the trigger is dataset-scoped,
 * with a tooltip explaining the restriction.
 */
export const ResourcePrefixSelector: React.FC<ResourcePrefixSelectorProps> = ({
  value,
  onChange,
  forEachPrimitive,
  disabled = false,
}) => {
  const isDatasetTrigger = forEachPrimitive === TriggerForEachPrimitive.Dataset;

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel id="resource-prefix-label">Resource Scope</InputLabel>
      <Select
        labelId="resource-prefix-label"
        id="resource-prefix-select"
        value={value}
        label="Resource Scope"
        onChange={(e) => onChange(e.target.value as ResourcePrefix)}
        data-cy="resource-prefix-select"
      >
        <MenuItem value="dataset">Dataset</MenuItem>
        {isDatasetTrigger ? (
          <Tooltip
            title="File-scoped conditions can only be used with per-file triggers. Change the trigger's 'For Each' setting to 'Dataset File' to enable this option."
            placement="right"
          >
            <span>
              <MenuItem
                value="file"
                disabled={true}
                className={styles.disabledMenuItem}
              >
                File
              </MenuItem>
            </span>
          </Tooltip>
        ) : (
          <MenuItem value="file">File</MenuItem>
        )}
      </Select>
    </FormControl>
  );
};
